import { FormEvent, useEffect, useId, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, CopyPlus, UserCog, XCircle } from 'lucide-react';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import {
  cancelShopShift,
  createReplacementShift,
  reassignShopShift,
} from '../../services/api';
import type { CashierDevice, CashierShift, ShopCashier } from '../../types';
import { deviceOptionLabel, fallbackDeviceLabel, shopDeviceErrorMessage } from '../../utils/cashierDevices';

type ActionTab = 'reassign' | 'cancel' | 'replacement';

type Props = {
  shift: CashierShift;
  cashiers: ShopCashier[];
  devices: CashierDevice[];
  onReassignSuccess: (shift: CashierShift) => void;
  onCancelSuccess: (shift: CashierShift) => void;
  onReplacementSuccess: (replacementShift: CashierShift) => void;
  onCancel?: () => void;
  initialTab?: ActionTab;
};

function getApiErrorMessage(e: unknown): string | null {
  const anyE = e as { response?: { data?: { message?: string } }; message?: string };
  return anyE?.response?.data?.message || anyE?.message || null;
}

export default function NoShowActionsPanel({
  shift,
  cashiers,
  devices,
  onReassignSuccess,
  onCancelSuccess,
  onReplacementSuccess,
  onCancel,
  initialTab = 'reassign',
}: Props) {
  const { timezone } = useTimezone();
  const [tab, setTab] = useState<ActionTab>(initialTab);
  const activeDevices = useMemo(
    () => devices.filter((d) => d.status === 'active'),
    [devices]
  );

  /** Keep the shift's device only while it is still approved; otherwise any approved device (""). */
  const defaultDeviceId = activeDevices.find((d) => d.id === shift.deviceId)?.id ?? '';

  const [cashierId, setCashierId] = useState(shift.cashierId);
  const [deviceId, setDeviceId] = useState(defaultDeviceId);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setTab(initialTab);
    setCashierId(shift.cashierId);
    setDeviceId(defaultDeviceId);
    setReason('');
  }, [shift.id, shift.cashierId, defaultDeviceId, initialTab]);

  const activeCashiers = useMemo(
    () => cashiers.filter((c) => c.status === 'active'),
    [cashiers]
  );

  const deviceNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of devices) map.set(d.id, d.deviceName);
    return map;
  }, [devices]);

  const onSubmitReassign = async (e: FormEvent) => {
    e.preventDefault();
    if (!cashierId) {
      toast.error('Please select a Cashier.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await reassignShopShift(shift.id, {
        cashierId,
        deviceId: deviceId || null,
        reason: reason.trim() || undefined,
      });
      if (!res.success || !res.data) throw new Error(shopDeviceErrorMessage(res, 'Reassign failed'));
      toast.success('Shift reassigned — status changed to scheduled.');
      onReassignSuccess(res.data);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || 'Reassign failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitCancel = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('Reason is required when cancelling a no-show shift.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await cancelShopShift(shift.id, { reason: reason.trim() });
      if (!res.success || !res.data) throw new Error(res.message || 'Cancel failed');
      toast.success('No-show shift cancelled.');
      onCancelSuccess(res.data);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || 'Cancel failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitReplacement = async (e: FormEvent) => {
    e.preventDefault();
    if (!cashierId) {
      toast.error('Please select a Cashier.');
      return;
    }
    if (!reason.trim()) {
      toast.error('Reason is required when creating a replacement shift.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createReplacementShift(shift.id, {
        cashierId,
        deviceId: deviceId || null,
        reason: reason.trim(),
      });
      if (!res.success || !res.data?.replacementShift) {
        throw new Error(shopDeviceErrorMessage(res, 'Replacement failed'));
      }
      toast.success('Replacement shift created.');
      onReplacementSuccess(res.data.replacementShift);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || 'Replacement failed');
    } finally {
      setSubmitting(false);
    }
  };

  const tabs: { id: ActionTab; label: string; icon: typeof UserCog }[] = [
    { id: 'reassign', label: 'Reassign', icon: UserCog },
    { id: 'replacement', label: 'Replacement', icon: CopyPlus },
    { id: 'cancel', label: 'Cancel', icon: XCircle },
  ];

  return (
    <div className="overflow-hidden rounded-2xl border border-rose-200 bg-white shadow-xl">
      <div className="flex items-start gap-3 border-b border-rose-200 bg-rose-50 px-4 py-3">
        <div className="rounded-lg bg-rose-100 p-2 text-rose-700">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-rose-900">No-show actions</h2>
          <p className="mt-0.5 text-sm text-rose-800/80">
            Reassign the same shift, create a replacement, or cancel with a reason.
          </p>
          {shift.noShowDetectedAt ? (
            <p className="mt-1 text-xs text-rose-700">
              No-show detected at {formatInTimezone(shift.noShowDetectedAt, timezone)}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-rose-200 bg-white px-4 py-2">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${
              tab === id
                ? 'bg-rose-600 text-white'
                : 'border border-rose-200 bg-white text-rose-800 hover:bg-rose-100'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'reassign' ? (
        <form onSubmit={(e) => void onSubmitReassign(e)}>
          <div className="grid gap-4 p-4 sm:grid-cols-2">
            <CashierDeviceFields
              cashierId={cashierId}
              deviceId={deviceId}
              shift={shift}
              activeCashiers={activeCashiers}
              activeDevices={activeDevices}
              deviceNameById={deviceNameById}
              onCashierChange={setCashierId}
              onDeviceChange={setDeviceId}
            />
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-stone-700">Reason (optional)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="e.g. Backup Cashier assigned for same slot"
                className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500"
              />
            </div>
          </div>
          <ActionFooter
            submitting={submitting}
            onCancel={onCancel}
            submitLabel={submitting ? 'Reassigning…' : 'Reassign shift'}
            submitClassName="bg-rose-600 hover:bg-rose-700"
          />
        </form>
      ) : null}

      {tab === 'replacement' ? (
        <form onSubmit={(e) => void onSubmitReplacement(e)}>
          <div className="grid gap-4 p-4 sm:grid-cols-2">
            <CashierDeviceFields
              cashierId={cashierId}
              deviceId={deviceId}
              shift={shift}
              activeCashiers={activeCashiers}
              activeDevices={activeDevices}
              deviceNameById={deviceNameById}
              onCashierChange={setCashierId}
              onDeviceChange={setDeviceId}
            />
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-stone-700">Reason *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                required
                placeholder="e.g. Original Cashier absent, replacement scheduled"
                className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500"
              />
            </div>
            <div className="sm:col-span-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
              The original no-show shift will be cancelled and a new scheduled shift created for the same time window.
            </div>
          </div>
          <ActionFooter
            submitting={submitting}
            onCancel={onCancel}
            submitLabel={submitting ? 'Creating…' : 'Create replacement'}
            submitClassName="bg-emerald-600 hover:bg-emerald-700"
          />
        </form>
      ) : null}

      {tab === 'cancel' ? (
        <form onSubmit={(e) => void onSubmitCancel(e)}>
          <div className="p-4">
            <label className="text-sm font-medium text-stone-700">Reason *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
              placeholder="e.g. Shift no longer needed, Cashier unavailable"
              className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500"
            />
            <p className="mt-2 text-xs text-stone-600">
              The shift will remain in history as cancelled. It cannot be deleted.
            </p>
          </div>
          <ActionFooter
            submitting={submitting}
            onCancel={onCancel}
            submitLabel={submitting ? 'Cancelling…' : 'Cancel shift'}
            submitClassName="bg-stone-700 hover:bg-stone-800"
          />
        </form>
      ) : null}
    </div>
  );
}

type FieldProps = {
  cashierId: string;
  deviceId: string;
  shift: CashierShift;
  activeCashiers: ShopCashier[];
  activeDevices: CashierDevice[];
  deviceNameById: Map<string, string>;
  onCashierChange: (id: string) => void;
  onDeviceChange: (id: string) => void;
};

function CashierDeviceFields({
  cashierId,
  deviceId,
  shift,
  activeCashiers,
  activeDevices,
  deviceNameById,
  onCashierChange,
  onDeviceChange,
}: FieldProps) {
  const deviceSelectId = useId();
  return (
    <>
      <div>
        <label className="text-sm font-medium text-stone-700">Assigned Cashier *</label>
        <select
          value={cashierId}
          onChange={(e) => onCashierChange(e.target.value)}
          required
          className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 outline-none focus:border-emerald-500"
        >
          <option value="">Select Cashier</option>
          {activeCashiers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.id === shift.cashierId ? ' (current)' : ''}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor={deviceSelectId} className="text-sm font-medium text-stone-700">
          Device (optional)
        </label>
        <select
          id={deviceSelectId}
          value={deviceId}
          onChange={(e) => onDeviceChange(e.target.value)}
          className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 outline-none focus:border-emerald-500"
        >
          <option value="">Any approved device (chosen at start)</option>
          {activeDevices.map((d) => (
            <option key={d.id} value={d.id}>
              {deviceOptionLabel(d)}
              {d.id === shift.deviceId ? ' (current)' : ''}
            </option>
          ))}
        </select>
        {shift.deviceId && !activeDevices.some((d) => d.id === shift.deviceId) ? (
          <p className="mt-1 text-xs text-amber-700">
            Current device ({deviceNameById.get(shift.deviceId) || fallbackDeviceLabel(shift.deviceId)}) is no
            longer active. Pick another device or leave “Any approved device”.
          </p>
        ) : (
          <p className="mt-1 text-xs text-stone-500">Pinning a device means the shift can only start there.</p>
        )}
      </div>
    </>
  );
}

function ActionFooter({
  submitting,
  onCancel,
  submitLabel,
  submitClassName,
}: {
  submitting: boolean;
  onCancel?: () => void;
  submitLabel: string;
  submitClassName: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-rose-200 bg-white px-4 py-3">
      {onCancel ? (
        <button
          type="button"
          className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-stone-50"
          onClick={onCancel}
          disabled={submitting}
        >
          Close
        </button>
      ) : null}
      <button
        type="submit"
        disabled={submitting}
        className={`rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-60 ${submitClassName}`}
      >
        {submitLabel}
      </button>
    </div>
  );
}
